﻿import React, { useState, useEffect, useRef } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Button } from "@foundation/components/ui/button";
import { Input } from "@foundation/components/ui/input";
import { Textarea } from "@foundation/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@foundation/components/ui/card";
import { Badge } from "@foundation/components/ui/badge";
import { Alert, AlertDescription } from "@foundation/components/ui/alert";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@foundation/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@foundation/components/ui/select";
import {
  Upload,
  X,
  Palette,
  Type,
  Globe,
  Mail,
  Phone,
  MapPin,
  Eye,
  Save,
  Loader2,
  ImageIcon,
  Check,
  Building2,
  Lock,
  GripHorizontal,
} from "lucide-react";
import { toast } from "@foundation/hooks/use-toast";
import BrandingService from "../../services/brandingService";
import {
  OrganizationBranding,
  CreateBrandingData,
  Organization,
} from "@foundation/types/organization";
import { getOrgContext, saveOrgContext } from "../../../features/revisr/services/organizationContextService";
import { toCssFontFamily } from "../../../features/revisr/utils/fontFamilyCss";

// Website URL is entered without a protocol (the UI shows a fixed "https://"
// prefix). These helpers keep the stored value canonical (full https:// URL)
// while letting the user type only the host/path, e.g. "www.revisr.ai/".
const stripUrlProtocol = (url: string): string =>
  url.replace(/^https?:\/\//i, "");

const normalizeWebsiteUrl = (url: string): string => {
  const trimmed = url.trim();
  if (!trimmed) return "";
  return `https://${stripUrlProtocol(trimmed)}`;
};

// Form validation schema
const brandingSchema = z.object({
  brand_title: z.string().optional(),
  tagline: z.string().optional(),
  primary_color: z
    .string()
    .optional()
    .refine(
      (val) => !val || BrandingService.isValidHexColor(val),
      "Must be a valid hex color (e.g., #FF5733)"
    ),
  secondary_color: z
    .string()
    .optional()
    .refine(
      (val) => !val || BrandingService.isValidHexColor(val),
      "Must be a valid hex color"
    ),
  accent_color: z
    .string()
    .optional()
    .refine(
      (val) => !val || BrandingService.isValidHexColor(val),
      "Must be a valid hex color"
    ),
  font_color: z
    .string()
    .optional()
    .refine(
      (val) => !val || BrandingService.isValidHexColor(val),
      "Must be a valid hex color"
    ),
  font_family: z.string().optional(),
  logo_position: z.enum(["left", "center", "right"]).optional(),
  theme_mode: z.enum(["light", "dark", "auto"]).optional(),
  website_url: z
    .string()
    .optional()
    .refine(
      (val) => !val || BrandingService.isValidUrl(normalizeWebsiteUrl(val)),
      "Enter a valid website address, e.g. www.example.com"
    ),
  contact_email: z
    .string()
    .optional()
    .refine(
      (val) => !val || BrandingService.isValidEmail(val),
      "Must be a valid email address"
    ),
  phone_number: z.string().optional(),
  address_street: z.string().optional(),
  address_city: z.string().optional(),
  address_state: z.string().optional(),
  address_postal_code: z.string().optional(),
  address_country: z.string().optional(),
  industry: z.string().optional(),
  org_size: z.string().optional(),
  maturityLevel: z.string().optional(),
  organizationContext: z.string().optional(),
});

type BrandingFormValues = z.infer<typeof brandingSchema>;

// No default logo — show the "No logo" placeholder when no custom logo is set.
// Previously this fell back to /favicon.png which confused users who removed their logo.
const DEFAULT_LOGO_URL: string | null = null;

// Default branding applied to new workspaces (colors, font)
const DEFAULT_BRANDING = {
  primary_color: '#000000',
  secondary_color: '#000000',
  font_color: '#000000',
  font_family: 'Inter',
} as const;

// Colour the live policy preview always uses. Generated policies render in black
// regardless of branding colours (matching the Boost Policy flow), so the preview
// is locked to black and does not follow the branding colour pickers.
const POLICY_PREVIEW_COLOR = '#000000';

const INDUSTRY_OPTIONS = [
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'construction', label: 'Construction' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'oil_gas', label: 'Oil & Gas' },
  { value: 'mining', label: 'Mining' },
  { value: 'transport_logistics', label: 'Transportation & Logistics' },
  { value: 'food_beverage', label: 'Food & Beverage' },
  { value: 'utilities', label: 'Utilities' },
  { value: 'government', label: 'Government' },
  { value: 'professional_services', label: 'Professional Services' },
  { value: 'other', label: 'Other' },
];

const SIZE_OPTIONS = [
  { value: '1-10', label: '1–10 employees' },
  { value: '11-50', label: '11–50 employees' },
  { value: '51-200', label: '51–200 employees' },
  { value: '201-500', label: '201–500 employees' },
  { value: '500+', label: '500+ employees' },
];


// —— Live Policy Preview ———————————————————————————————————————————————————————————————

type PolicyPreviewProps = {
  primaryColor: string;
  secondaryColor: string;
  fontColor: string;
  fontFamily: string;
  brandTitle: string;
  tagline: string;
  logoUrl: string | null;
};

const PolicyPreview = ({
  primaryColor,
  secondaryColor,
  fontColor,
  fontFamily,
  brandTitle,
  tagline,
  logoUrl,
}: PolicyPreviewProps) => {
  const toRgba = (hex: string, alpha: number) => {
    const h = hex.replace('#', '');
    const r = parseInt(h.substring(0, 2), 16);
    const g = parseInt(h.substring(2, 4), 16);
    const b = parseInt(h.substring(4, 6), 16);
    if (isNaN(r) || isNaN(g) || isNaN(b)) return `rgba(0,0,0,${alpha})`;
    return `rgba(${r},${g},${b},${alpha})`;
  };

  const trans = 'all 0.25s ease';

  return (
    <div className="sticky top-6 select-none">
      {/* Header label */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Eye className="w-4 h-4" style={{ color: '#6b7280' }} />
          <span className="text-sm font-semibold text-gray-800">Live Preview</span>
        </div>
        <span className="text-xs text-gray-400">Updates instantly</span>
      </div>

      {/* Note: policies render in black regardless of branding colours */}
      <div className="flex items-center gap-2 mb-4 px-3 py-2 rounded-lg bg-gray-50 border border-gray-100">
        <span className="text-[11px] text-gray-500">
          Policies are generated in black text — branding colours don&apos;t change the policy body.
        </span>
      </div>

      {/* A4-style document card */}
      <div
        className="policy-preview-paper rounded-lg overflow-hidden border border-gray-200 shadow-sm"
        style={{ fontFamily: toCssFontFamily(fontFamily), background: '#ffffff' }}
      >
        {/* — Letterhead ——————————————————————————————————————————————————————————————— */}
        <div className="bg-white px-5 pt-4 pb-0">
          <div className="flex items-start justify-between gap-3 pb-4">
            {/* Logo + name */}
            <div className="flex items-center gap-3 min-w-0">
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt="logo"
                  style={{
                    height: 36,
                    width: 'auto',
                    maxWidth: 80,
                    objectFit: 'contain',
                    display: 'block',
                    flexShrink: 0,
                  }}
                />
              ) : (
                <div
                  className="h-9 w-9 rounded-md flex items-center justify-center text-sm font-bold flex-shrink-0"
                  style={{ background: toRgba(primaryColor, 0.12), color: primaryColor, transition: trans }}
                >
                  {brandTitle.charAt(0).toUpperCase() || 'O'}
                </div>
              )}
              <div className="min-w-0">
                <p className="text-[13px] font-bold text-gray-900 leading-tight truncate">
                  {brandTitle || 'Your Organisation'}
                </p>
                {tagline && (
                  <p className="text-[10px] text-gray-400 leading-tight truncate mt-0.5">{tagline}</p>
                )}
              </div>
            </div>
            {/* Doc meta */}
            <div className="text-right flex-shrink-0">
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Document</p>
              <p className="text-[10px] text-gray-400">Rev 1.0 · 2025</p>
            </div>
          </div>
          {/* Primary color rule under letterhead */}
          <div style={{ height: 3, background: primaryColor, margin: '0 -20px', transition: trans }} />
        </div>

        {/* — Document title ——————————————————————————————————————————————————————————————— */}
        <div className="px-5 py-3" style={{ background: toRgba(primaryColor, 0.05), transition: trans }}>
          <p
            className="text-[12px] font-bold uppercase tracking-wider"
            style={{ color: primaryColor, transition: trans }}
          >
            Health &amp; Safety Management Policy
          </p>
        </div>

        {/* — Body ——————————————————————————————————————————————————————————————— */}  
        <div className="px-5 py-4 space-y-3 bg-white">

          {/* Section heading helper */}
          {(['1. Purpose', '2. Scope', '3. Responsibilities'] as const).map((title, i) => {
            const bodies = [
              'This policy establishes the framework for managing workplace health, safety, and environmental obligations.',
              `Applies to all employees, contractors, and visitors operating within premises controlled by ${brandTitle || 'the organisation'}.`,
              'Management is responsible for providing resources, training, and ensuring compliance with applicable legislation.',
            ];
            return (
              <React.Fragment key={title}>
                <div>
                  <div
                    className="flex items-center gap-2 mb-1"
                    style={{ borderLeft: `3px solid ${primaryColor}`, paddingLeft: 8, transition: trans }}
                  >
                    <p
                      className="text-[11px] font-bold"
                      style={{ color: secondaryColor, transition: trans }}
                    >
                      {title}
                    </p>
                  </div>
                  <p
                    className="text-[11px] leading-relaxed pl-3"
                    style={{ color: fontColor, transition: trans }}
                  >
                    {bodies[i]}
                  </p>
                </div>
                {/* Callout after section 1 */}
                {i === 0 && (
                  <div
                    className="rounded-md px-3 py-2.5"
                    style={{
                      background: toRgba(secondaryColor, 0.08),
                      borderLeft: `3px solid ${secondaryColor}`,
                      transition: trans,
                    }}
                  >
                    <p
                      className="text-[10px] font-bold uppercase tracking-wide mb-0.5"
                      style={{ color: secondaryColor, transition: trans }}
                    >
                      Key Requirement
                    </p>
                    <p
                      className="text-[11px] leading-relaxed"
                      style={{ color: fontColor, transition: trans }}
                    >
                      All personnel must complete safety induction before commencing work.
                    </p>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* —— Footer ——————————————————————————————————————————————————————————————— */}
        <div
          className="px-5 py-2.5 flex items-center justify-between"
          style={{ background: toRgba(secondaryColor, 0.1), borderTop: `1px solid ${toRgba(secondaryColor, 0.2)}`, transition: trans }}
        >
          <p
            className="text-[10px] font-semibold uppercase tracking-wide"
            style={{ color: secondaryColor, transition: trans }}
          >
            {brandTitle || 'Organisation'} · Confidential
          </p>
          <p className="text-[10px] text-gray-400">Page 1 of 1</p>
        </div>
      </div>
    </div>
  );
};

// —— ColorInput ———————————————————————————————————————————————————————————————
// Defined OUTSIDE BrandingEditor so its component identity is stable across
// re-renders. Defining it inside would cause React to unmount/remount the input
// on every form change, losing focus after each character typed.

type ColorInputProps = {
  value: string;
  onChange: (value: string) => void;
  label: string;
};

const ColorInput = ({ value, onChange }: ColorInputProps) => {
  const [localText, setLocalText] = React.useState(value || '');
  const [focused, setFocused] = React.useState(false);

  // Sync display text from parent only when the field is not focused
  // (prevents color-picker changes from interrupting mid-type)
  React.useEffect(() => {
    if (!focused) setLocalText(value || '');
  }, [value, focused]);

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let raw = e.target.value;

    // Auto-prepend # so users can type 6 digits without the leading #
    if (raw && !raw.startsWith('#')) raw = `#${raw}`;

    setLocalText(raw);

    // Propagate to parent only when a complete valid hex is typed
    if (raw && BrandingService.isValidHexColor(raw)) {
      onChange(raw);
    } else if (!raw) {
      onChange('');
    }
  };

  const handleBlur = () => {
    setFocused(false);
    // Snap display back to the last valid parent value on blur
    setLocalText(value || '');
  };

  return (
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={value || '#000000'}
        onChange={(e) => onChange(e.target.value)}
        className="w-10 h-10 rounded border border-border cursor-pointer flex-shrink-0"
        style={{ padding: 0 }}
      />
      <Input
        value={localText}
        onChange={handleTextChange}
        onFocus={() => setFocused(true)}
        onBlur={handleBlur}
        placeholder="#000000"
        maxLength={7}
        className="flex-1 font-mono"
        spellCheck={false}
        autoComplete="off"
      />
    </div>
  );
};

interface BrandingEditorProps {
  organization: Organization;
  onSuccess?: () => void;
  initialTab?: "visual" | "contact" | "profile";
}

export function BrandingEditor({
  organization,
  onSuccess,
  initialTab,
}: BrandingEditorProps) {
  const [branding, setBranding] = useState<OrganizationBranding | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"visual" | "contact" | "profile">(
    initialTab ?? "visual"
  );

  const isOrgAdmin = organization.role === 'admin' || organization.role === 'org_admin';
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Logo resize state — intercepts file selection before upload
  const [pendingLogoFile, setPendingLogoFile] = useState<File | null>(null);
  const [pendingLogoUrl, setPendingLogoUrl] = useState<string>('');
  const [logoOutputWidth, setLogoOutputWidth] = useState(256);
  // Natural dimensions of the pending logo file
  const [logoNaturalW, setLogoNaturalW] = useState(0);
  const [logoNaturalH, setLogoNaturalH] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  // Natural dimensions of the currently saved logo
  const [currentLogoW, setCurrentLogoW] = useState<number | null>(null);
  const [currentLogoH, setCurrentLogoH] = useState<number | null>(null);
  const logoResizeImgRef = useRef<HTMLImageElement>(null);
  const dragRef = useRef<{ startX: number; startWidth: number } | null>(null);

  const form = useForm<BrandingFormValues>({
    resolver: zodResolver(brandingSchema),
    defaultValues: {},
  });

  // Load existing branding
  useEffect(() => {
    loadBranding();
  }, [organization.id]);

  const loadBranding = async () => {
    try {
      setLoading(true);
      // Org profile fields (industry/org_size/jurisdiction/standards) are stored in
      // base_tenants.organization_context.profile. Load them in parallel with branding
      // and prefer them over the legacy branding-table copies for back-compat.
      const [brandingData, orgContext] = await Promise.all([
        BrandingService.getOrganizationBranding(organization.id),
        getOrgContext(organization.id).catch((err) => {
          console.warn("Failed to load org context, falling back to branding fields:", err);
          return null;
        }),
      ]);

      const profile = orgContext?.profile;

      if (brandingData) {
        setBranding(brandingData);
        // Apply cache-busting to logo URL to avoid stale browser cache after upload;
        // fall back to the default logo when no custom logo is set
        setLogoPreview(
          brandingData.logo_url
            ? BrandingService.getLogoUrlWithCacheBusting(brandingData.logo_url)
            : DEFAULT_LOGO_URL
        );

        // Populate form with existing data, using defaults where values are absent
        form.reset({
          brand_title: brandingData.brand_title || "",
          tagline: brandingData.tagline || "",
          primary_color: brandingData.primary_color || DEFAULT_BRANDING.primary_color,
          secondary_color: brandingData.secondary_color || DEFAULT_BRANDING.secondary_color,
          accent_color: brandingData.accent_color || "",
          font_color: brandingData.font_color || DEFAULT_BRANDING.font_color,
          font_family: brandingData.font_family || DEFAULT_BRANDING.font_family,
          logo_position: brandingData.logo_position || "left",
          theme_mode: brandingData.theme_mode || "light",
          website_url: stripUrlProtocol(brandingData.website_url || ""),
          contact_email: brandingData.contact_email || "",
          phone_number: brandingData.phone_number || "",
          address_street: brandingData.address?.street || "",
          address_city: brandingData.address?.city || "",
          address_state: brandingData.address?.state || "",
          address_postal_code: brandingData.address?.postal_code || "",
          address_country: brandingData.address?.country || "",
          industry: profile?.industry ?? brandingData.industry ?? "",
          org_size: profile?.org_size ?? brandingData.org_size ?? "",
          maturityLevel: profile?.maturityLevel ?? "",
          organizationContext: orgContext?.organizationContext ?? "",
        });
      } else {
        // No branding record yet — show defaults, but still honour any org-context profile
        setLogoPreview(DEFAULT_LOGO_URL);
        form.reset({
          ...DEFAULT_BRANDING,
          logo_position: 'left',
          theme_mode: 'light',
          industry: profile?.industry ?? "",
          org_size: profile?.org_size ?? "",
          maturityLevel: profile?.maturityLevel ?? "",
          organizationContext: orgContext?.organizationContext ?? "",
        });
      }
    } catch (error) {
      console.error("Error loading branding:", error);
      toast({
        title: "Error",
        description: "Failed to load branding data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const cancelLogoResize = () => {
    if (pendingLogoUrl) URL.revokeObjectURL(pendingLogoUrl);
    setPendingLogoFile(null);
    setPendingLogoUrl('');
    setLogoNaturalW(0);
    setLogoNaturalH(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const confirmLogoResize = () => {
    if (!pendingLogoFile || !logoResizeImgRef.current) return;
    const img = logoResizeImgRef.current;
    const scale = logoOutputWidth / img.naturalWidth;
    const outputH = Math.round(img.naturalHeight * scale);
    const canvas = document.createElement('canvas');
    canvas.width = logoOutputWidth;
    canvas.height = outputH;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(img, 0, 0, logoOutputWidth, outputH);
    canvas.toBlob(
      async (blob) => {
        if (!blob) return;
        const resizedFile = new File([blob], pendingLogoFile.name, { type: 'image/png' });
        cancelLogoResize();
        await uploadLogoFile(resizedFile);
      },
      'image/png'
    );
  };

  const uploadLogoFile = async (file: File) => {
    try {
      setUploading(true);
      console.log(`[BrandingEditor] ðŸ“¤ Starting logo upload for organization: ${organization.id}`);

      // Create preview
      const previewUrl = URL.createObjectURL(file);
      setLogoPreview(previewUrl);

      // Upload to storage
      console.log(`[BrandingEditor] â˜ï¸ Uploading file to storage...`);
      const result = await BrandingService.uploadOrganizationLogo(organization.id, file);

      if (result.success && result.url) {
        console.log(`[BrandingEditor] âœ… File uploaded successfully: ${result.url}`);
        await BrandingService.upsertOrganizationBranding(organization.id, { logo_url: result.url });
        const cacheBustedUrl = BrandingService.getLogoUrlWithCacheBusting(result.url);
        setLogoPreview(cacheBustedUrl);
        toast({ title: 'Success', description: 'Logo uploaded successfully' });
        // Pass the new URL directly so WorkspaceSwitcher updates instantly — no extra DB fetch.
        window.dispatchEvent(new CustomEvent('branding-updated', {
          detail: { orgId: organization.id, logoUrl: cacheBustedUrl },
        }));
        console.log(`[BrandingEditor] âœ… Logo upload process completed successfully`);
      } else {
        console.error(`[BrandingEditor] âŒ File upload failed:`, result.error);
        throw new Error(result.error || 'Upload failed');
      }
    } catch (error) {
      console.error('[BrandingEditor] âŒ Logo upload error:', error);
      let errorMessage = 'Failed to upload logo';
      if (error instanceof Error) {
        if (error.message.includes('permission denied')) {
          errorMessage = 'Permission denied. You may need admin access to update branding.';
        } else if (error.message.includes('storage')) {
          errorMessage = 'Failed to upload file to storage. Please try again.';
        } else {
          errorMessage = error.message;
        }
      }
      toast({ title: 'Error', description: errorMessage, variant: 'destructive' });
      setLogoPreview(
        branding?.logo_url ? BrandingService.getLogoUrlWithCacheBusting(branding.logo_url) : DEFAULT_LOGO_URL
      );
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleLogoUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Show resize panel before uploading
    // Default output width to the current logo's width (if known) so preference is retained
    const defaultW = currentLogoW ?? 256;
    const objectUrl = URL.createObjectURL(file);
    setPendingLogoFile(file);
    setPendingLogoUrl(objectUrl);
    setLogoNaturalW(0);
    setLogoNaturalH(0);
    setLogoOutputWidth(defaultW);
  };

  const handleResizeDragStart = (e: React.MouseEvent) => {
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startWidth: logoOutputWidth };
    setIsDragging(true);

    const onMove = (mv: MouseEvent) => {
      if (!dragRef.current) return;
      const delta = mv.clientX - dragRef.current.startX;
      const scale = Math.max(1, dragRef.current.startWidth / 300);
      const newW = Math.max(32, Math.min(2048, Math.round(dragRef.current.startWidth + delta * scale)));
      setLogoOutputWidth(newW);
    };

    const onUp = () => {
      dragRef.current = null;
      setIsDragging(false);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const handleRemoveLogo = async () => {
    try {
      setUploading(true);

      // Delete from storage
      await BrandingService.deleteLogoFromStorage(organization.id);

      // Update branding to remove logo URL
      if (branding) {
        await BrandingService.updateOrganizationBranding({
          id: branding.id,
          logo_url: null,
        });
      }

      setLogoPreview(DEFAULT_LOGO_URL);

      toast({
        title: "Success",
        description: "Logo removed successfully",
      });

      await loadBranding();
      // logoUrl: null signals removal — switcher clears immediately, no fetch needed.
      window.dispatchEvent(new CustomEvent('branding-updated', {
        detail: { orgId: organization.id, logoUrl: null },
      }));
    } catch (error) {
      console.error("Error removing logo:", error);
      toast({
        title: "Error",
        description: "Failed to remove logo",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const onSubmit = async (values: BrandingFormValues) => {
    try {
      setSaving(true);

      // Prepare branding data — note: industry/org_size/jurisdiction/standards are
      // intentionally omitted here. The branding RPC writes only to
      // base_organisation_branding; those four fields belong in
      // base_tenants.organization_context and are saved separately below.
      const brandingData: Partial<CreateBrandingData> = {
        brand_title: values.brand_title || undefined,
        tagline: values.tagline || undefined,
        primary_color: values.primary_color || undefined,
        secondary_color: values.secondary_color || undefined,
        accent_color: values.accent_color || undefined,
        font_color: values.font_color || undefined,
        font_family: values.font_family || undefined,
        logo_position: values.logo_position || undefined,
        theme_mode: values.theme_mode || undefined,
        website_url: values.website_url ? normalizeWebsiteUrl(values.website_url) : undefined,
        contact_email: values.contact_email || undefined,
        phone_number: values.phone_number || undefined,
        address: {
          street: values.address_street || undefined,
          city: values.address_city || undefined,
          state: values.address_state || undefined,
          postal_code: values.address_postal_code || undefined,
          country: values.address_country || undefined,
        },
      };

      // Clean up empty address object
      if (Object.values(brandingData.address || {}).every((v) => !v)) {
        brandingData.address = undefined;
      }

      await BrandingService.upsertOrganizationBranding(
        organization.id,
        brandingData
      );

      // Save org profile + organizationContext to base_tenants.organization_context
      const existingCtx = await getOrgContext(organization.id).catch(() => null);
      await saveOrgContext(
        {
          ...existingCtx,
          organizationContext: values.organizationContext || undefined,
          profile: {
            ...existingCtx?.profile,
            industry: values.industry || undefined,
            org_size: values.org_size || undefined,
            maturityLevel: values.maturityLevel || undefined,
          },
        },
        organization.id
      );

      toast({
        title: "Success",
        description: "Branding updated successfully",
      });

      await loadBranding();
      // Notify workspace switcher and other consumers to refresh the logo immediately
      window.dispatchEvent(new CustomEvent('branding-updated', { detail: { orgId: organization.id } }));
      onSuccess?.();
    } catch (error) {
      console.error("Error saving branding:", error);
      toast({
        title: "Error",
        description: "Failed to save branding",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  // ColorInput is defined outside this component (stable identity, no remount on re-render)

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin" />
        <span className="ml-2">Loading branding...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Organisation Branding</h3>
          <p className="text-sm text-muted-foreground">
            Customise your organisation's visual identity and contact
            information
          </p>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* Tabs */}
          <div className="flex space-x-1 bg-muted p-1 rounded-lg">
            <button
              type="button"
              onClick={() => setActiveTab("visual")}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === "visual"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Palette className="h-4 w-4" />
              Visual Branding
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("contact")}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === "contact"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Mail className="h-4 w-4" />
              Contact Info
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("profile")}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === "profile"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Building2 className="h-4 w-4" />
              Org Profile
            </button>
          </div>

          {/* Read-only notice for non-admins */}
          {!isOrgAdmin && (
            <Alert>
              <Lock className="h-4 w-4" />
              <AlertDescription>
                You have read-only access. Only organisation admins can edit branding settings.
              </AlertDescription>
            </Alert>
          )}

          {/* All form fields — disabled for non-admins via fieldset */}
          <fieldset disabled={!isOrgAdmin} className="border-0 p-0 m-0 min-w-0">

          {/* Visual Branding Tab */}
          {activeTab === "visual" && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', alignItems: 'start' }}>
            <div className="space-y-6">
              {/* Logo Section */}
              <Card className="border-border">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ImageIcon className="h-5 w-5" />
                    Logo
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Hidden file input — always mounted */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />

                  {/* Normal logo display — shown when no file is pending */}
                  {!pendingLogoFile && (
                    <div className="flex items-center gap-4">
                      <div className="flex-1">
                        {logoPreview ? (
                          <div className="relative inline-block">
                            <img
                              src={logoPreview}
                              alt="Organisation Logo"
                              className="w-32 h-32 object-contain border rounded-lg bg-white"
                              onLoad={e => {
                                const img = e.currentTarget;
                                setCurrentLogoW(img.naturalWidth);
                                setCurrentLogoH(img.naturalHeight);
                              }}
                            />
                            <Button
                              type="button"
                              variant="destructive"
                              size="sm"
                              className="absolute -top-2 -right-2 h-6 w-6 rounded-full p-0"
                              onClick={handleRemoveLogo}
                              disabled={uploading || !isOrgAdmin}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        ) : (
                          <div className="w-32 h-32 border-2 border-dashed border-muted-foreground/25 rounded-lg flex items-center justify-center">
                            <div className="text-center">
                              <ImageIcon className="h-8 w-8 mx-auto text-muted-foreground/50" />
                              <p className="text-sm text-muted-foreground mt-1">No logo</p>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 space-y-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploading || !isOrgAdmin}
                          className="w-full"
                        >
                          {uploading ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Uploading...
                            </>
                          ) : (
                            <>
                              <Upload className="h-4 w-4 mr-2" />
                              Upload Logo
                            </>
                          )}
                        </Button>
                        <p className="text-xs text-muted-foreground">PNG, JPG, or SVG. Max 5MB.</p>
                      </div>
                    </div>
                  )}

                  {/* Inline resize panel — shown after file selected, before upload */}
                  {pendingLogoFile && (() => {
                    const aspect = logoNaturalW > 0 ? logoNaturalH / logoNaturalW : 1;
                    const outputH = Math.round(logoOutputWidth * aspect);
                    const previewMaxW = 300;
                    const previewW = Math.min(logoOutputWidth, previewMaxW);
                    return (
                      <div className="rounded-xl border border-border bg-card overflow-hidden">
                        {/* Header */}
                        <div className="px-4 pt-4 pb-3 flex items-start justify-between border-b border-border">
                          <div>
                            <p className="text-sm font-semibold text-foreground">Resize Logo</p>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {logoNaturalW > 0
                                ? `Original: ${logoNaturalW} × ${logoNaturalH} px`
                                : 'Loading image…'}
                            </p>
                          </div>
                          <button type="button" onClick={cancelLogoResize} className="text-muted-foreground hover:text-foreground transition-colors mt-0.5">
                            <X className="h-4 w-4" />
                          </button>
                        </div>

                        {/* Preview */}
                        <div className="p-4 flex flex-col items-center gap-3 bg-background">
                          {/* Image container — sized to fit the image */}
                          <div className="relative inline-flex select-none">
                            <img
                              ref={logoResizeImgRef}
                              src={pendingLogoUrl}
                              alt="Logo preview"
                              style={{ width: previewW, height: 'auto', maxHeight: 200, objectFit: 'contain', display: 'block', backgroundColor: 'white' }}
                              draggable={false}
                              onLoad={e => {
                                const img = e.currentTarget;
                                setLogoNaturalW(img.naturalWidth);
                                setLogoNaturalH(img.naturalHeight);
                              }}
                            />
                            {/* Drag-to-resize handle */}
                            <div
                              onMouseDown={handleResizeDragStart}
                              className={`absolute -bottom-2.5 -right-2.5 w-5 h-5 rounded flex items-center justify-center cursor-ew-resize transition-colors shadow-sm ${
                                isDragging
                                  ? 'bg-primary text-primary-foreground'
                                  : 'bg-background border border-border text-muted-foreground hover:bg-muted hover:text-foreground'
                              }`}
                              title="Drag left/right to resize"
                            >
                              <GripHorizontal className="w-3 h-3" />
                            </div>
                          </div>
                          {/* Output dimensions + drag hint */}
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-mono bg-muted text-muted-foreground rounded px-2 py-0.5">
                              {logoOutputWidth} × {outputH || '—'} px
                            </span>
                            {logoOutputWidth > previewMaxW && (
                              <span className="text-[10px] text-amber-500">scaled down</span>
                            )}
                            <span className="text-[10px] text-muted-foreground">· drag to resize</span>
                          </div>
                        </div>

                        <div className="px-4 pb-4">
                          {/* Actions */}
                          <div className="flex gap-2 pt-1 border-t border-border">
                            <Button variant="outline" size="sm" className="flex-1 mt-2" onClick={cancelLogoResize}>
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              className="flex-1 mt-2"
                              onClick={confirmLogoResize}
                              disabled={uploading || logoNaturalW === 0}
                            >
                              <Check className="h-3.5 w-3.5 mr-1.5" />
                              {uploading ? 'Uploading…' : `Upload at ${logoOutputWidth} px`}
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                </CardContent>
              </Card>

              {/* Text Branding */}
              <Card className="border-border">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Type className="h-5 w-5" />
                    Text Branding
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="tagline"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Tagline</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Your brand tagline or slogan"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="font_family"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Font Family</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select font" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {BrandingService.getAvailableFonts().map((font) => (
                              <SelectItem key={font.value} value={font.value}>
                                {font.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              {/* Colors */}
              <Card className="border-border">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Palette className="h-5 w-5" />
                    Brand Colors
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="primary_color"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Primary Color</FormLabel>
                        <FormControl>
                          <ColorInput
                            value={field.value || ""}
                            onChange={field.onChange}
                            label="Primary"
                          />
                        </FormControl>
                        <FormDescription>Header banner, section headings &amp; accent bars</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="secondary_color"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Secondary Color</FormLabel>
                        <FormControl>
                          <ColorInput
                            value={field.value || ""}
                            onChange={field.onChange}
                            label="Secondary"
                          />
                        </FormControl>
                        <FormDescription>Callout boxes &amp; document footer</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="font_color"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Font Color</FormLabel>
                        <FormControl>
                          <ColorInput
                            value={field.value || ""}
                            onChange={field.onChange}
                            label="Font"
                          />
                        </FormControl>
                        <FormDescription>All body &amp; paragraph text</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
            </div>

            {/* —— Right column: Live Policy Preview —— */}
            <PolicyPreview
              // Policies render in black regardless of branding colours (matching the
              // Boost Policy flow), so the preview is intentionally NOT bound to the
              // branding colour pickers — only logo/font/title/tagline update live.
              primaryColor={form.watch('primary_color') || DEFAULT_BRANDING.primary_color}
              secondaryColor={form.watch('secondary_color') || DEFAULT_BRANDING.secondary_color}
              fontColor={form.watch('font_color') || DEFAULT_BRANDING.font_color}
              fontFamily={form.watch('font_family') || DEFAULT_BRANDING.font_family}
              brandTitle={form.watch('brand_title') || organization.name}
              tagline={form.watch('tagline') || ''}
              logoUrl={logoPreview}
            />
          </div>
          )}

          {/* Contact Info Tab */}
          {activeTab === "contact" && (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="h-5 w-5" />
                    Contact Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="website_url"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Website URL</FormLabel>
                        <FormControl>
                          <div className="flex">
                            <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-muted text-sm text-muted-foreground select-none">
                              https://
                            </span>
                            <Input
                              placeholder="www.example.com"
                              className="rounded-l-none"
                              {...field}
                              onChange={(e) =>
                                field.onChange(stripUrlProtocol(e.target.value))
                              }
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="contact_email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Contact Email</FormLabel>
                        <FormControl>
                          <Input placeholder="contact@example.com" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="phone_number"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Phone Number</FormLabel>
                        <FormControl>
                          <Input placeholder="+1 (555) 123-4567" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="h-5 w-5" />
                    Address
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="address_street"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Street Address</FormLabel>
                        <FormControl>
                          <Input placeholder="123 Main Street" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="address_city"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>City</FormLabel>
                          <FormControl>
                            <Input placeholder="New York" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="address_state"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>State/Province</FormLabel>
                          <FormControl>
                            <Input placeholder="NY" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="address_postal_code"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Postal Code</FormLabel>
                          <FormControl>
                            <Input placeholder="10001" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="address_country"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Country</FormLabel>
                          <FormControl>
                            <Input placeholder="United States" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Organisation Profile Tab */}
          {activeTab === "profile" && (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="h-5 w-5" />
                    Organisation Profile
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Industry */}
                    <FormField
                      control={form.control}
                      name="industry"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Industry</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value || ""}
                            disabled={!isOrgAdmin}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select industry" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {INDUSTRY_OPTIONS.map((opt) => (
                                <SelectItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Organisation Size */}
                    <FormField
                      control={form.control}
                      name="org_size"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Organisation Size</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value || ""}
                            disabled={!isOrgAdmin}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select size" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {SIZE_OPTIONS.map((opt) => (
                                <SelectItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Maturity Level */}
                  <FormField
                    control={form.control}
                    name="maturityLevel"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Governance Maturity</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value || ""}
                          disabled={!isOrgAdmin}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select maturity level…" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="low">Low</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="high">High</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Organisation Context */}
                  <div className="pt-2 border-t border-border">
                    <FormField
                      control={form.control}
                      name="organizationContext"
                      render={({ field }: { field: React.InputHTMLAttributes<HTMLTextAreaElement> & { value?: string } }) => (
                        <FormItem>
                          <FormLabel className="text-base font-semibold text-foreground">
                            Policy Tailoring Notes
                          </FormLabel>
                          <FormDescription>
                            Provide extra details to further tailor generated policies{' '}
                            <span className="text-muted-foreground">(Optional)</span>
                          </FormDescription>
                          <p className="text-sm text-muted-foreground -mt-1 mb-1">
                            e.g. specific regulatory requirements, internal processes, risk appetite, governance structure, or anything the AI should emphasise or avoid.
                          </p>
                          <FormControl>
                            <Textarea
                              placeholder="Add any organisation-specific context here…"
                              className="min-h-[150px] resize-y"
                              {...field}
                              disabled={!isOrgAdmin}
                            />
                          </FormControl>
                          <p className="text-xs text-muted-foreground">
                            This is combined with your Org Profile above when generating policies.
                          </p>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          </fieldset>

          {/* Save Button */}
          <div className="flex justify-end space-x-2">
            <Button type="submit" disabled={saving || !isOrgAdmin}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Branding
                </>
              )}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
 

